import { DASH_META_ROOT, getAuthDashMetaCsrfToken } from '@revelio/auth';

interface CreateSharedReportPayload {
  name: string;
  pageConfig: Record<string, unknown>;
  chartData: Record<string, unknown>;
  pageState: Record<string, unknown>;
}

interface CreateSharedReportResponse {
  token: string;
}

export interface SharedReportSnapshot {
  token: string;
  name: string;
  pageConfig: Record<string, unknown>;
  chartData: Record<string, unknown>;
  pageState: Record<string, unknown>;
  created: string;
}

/** Thrown when the shared-reports POST endpoint returns 429. */
export class SharedReportRateLimitError extends Error {
  /** Seconds the client should wait before retrying, from the `Retry-After` header. */
  readonly retryAfterSeconds: number;

  constructor(retryAfterSeconds: number) {
    super('Shared report creation rate-limited');
    this.name = 'SharedReportRateLimitError';
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

const DEFAULT_RETRY_AFTER_SECONDS = 60;

export const createSharedReport = async (
  payload: CreateSharedReportPayload
): Promise<CreateSharedReportResponse> => {
  const csrfToken = getAuthDashMetaCsrfToken();

  const response = await fetch(`${DASH_META_ROOT}/api/shared-reports`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'x-surf-token-dash-meta': csrfToken ?? '',
    },
    body: JSON.stringify(payload),
  });

  if (response.status === 429) {
    const retryAfter = Number(response.headers.get('Retry-After'));
    throw new SharedReportRateLimitError(
      Number.isFinite(retryAfter) && retryAfter > 0
        ? retryAfter
        : DEFAULT_RETRY_AFTER_SECONDS
    );
  }

  if (!response.ok) {
    throw new Error('Failed to create shared report');
  }

  return response.json();
};

export const getSharedReport = async (
  token: string
): Promise<SharedReportSnapshot> => {
  const response = await fetch(`${DASH_META_ROOT}/api/shared-reports/${token}`);

  if (!response.ok) {
    throw new Error('Shared report not found');
  }

  return response.json();
};
